// owner-map: OP-03a.spa
import { makeRun, STATUS, type ForecastEvent } from "../lib/forecast/model.js";
import { rowToRun } from "../lib/forecast/hydrate.js";
import { runCreated, runErrored, runRemoved } from "../lib/forecast/reducer.js";
import { pickedTier } from "../lib/forecast/engines.js";
import { selectRun } from "../lib/forecast/selectors.js";
import { triggerToAssumption } from "../lib/forecast/branchTrigger.js";
import {
  derivedSense,
  derivedSourceSnapshot,
  isDerivable,
  isMultiwayDerivation,
  sourceEventIdentity,
} from "../lib/forecast/derivedBranch.js";
import {
  forecastPointers,
  latestForecastPointer,
  liveContractMomentOf,
  liveForecastPointerOf,
} from "../lib/forecast/reportPointer.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import {
  beginIntent,
  type BeginIntentOptions,
  type IntentHandle,
} from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";

type AnyFn = (...args: any[]) => any;

export interface DeriveBranchDeps extends CommandDeps {
  callbacks: { startBranchAnim: AnyFn };
}

export interface DeriveBranchArgs {
  parentRunId: string;
  event: ForecastEvent;
  newValue: number;
  forkDate: string | null;
  clientId?: string;
  signal?: AbortSignal;
  onComplete?: AnyFn;
  prefetch?: DerivedHeadlinePrefetch | null;
  /** The control that started it (telemetry `origin`). */
  origin?: BeginIntentOptions["origin"];
}

/**
 * The parent headline a derivation freezes: a binary parent's P(YES), or a multiway parent's
 * distribution mass. Which one is decided by the parent's form, never by the value's shape.
 */
export type DerivedHeadline = number | number[];

export interface DerivedHeadlinePrefetch {
  rowKey: string;
  headline: Promise<DerivedHeadline | null>;
  startedAt: number;
}

type HeadlineReadOptions = { signal?: AbortSignal; onUnauthorized?: () => void };

// Both reads are the same storage read-through GET; neither runs a model.
function readParentHeadline(
  deps: CommandDeps,
  parent: Parameters<typeof isMultiwayDerivation>[0],
  rowKey: string,
  options: HeadlineReadOptions,
): Promise<DerivedHeadline | null> {
  return isMultiwayDerivation(parent)
    ? deps.services.api.getForecastReportMass(rowKey, options)
    : deps.services.api.getForecastReportHeadline(rowKey, options);
}

/**
 * The server re-reads the same report inside the derive and rejects a drifted
 * `parentProbability` as `source_changed`, so a stale prefetch can only cost the user a retry,
 * never a wrong row.
 */
export const DERIVED_HEADLINE_PREFETCH_TTL_MS = 60_000;

export interface DeriveBranchResult {
  handled: boolean;
  fallback: boolean;
  runId: string | null;
  /** On a fallback: the open branch intent the model run continues (`branchForecast`). */
  intent?: IntentHandle<"forecast.branch">;
  /** On a fallback: the branch id the intent named, which the model run reuses. */
  clientId?: string;
}

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

type DerivedParent = {
  createdAt?: string | null;
  history?: unknown[];
  reportPointers?: unknown[] | null;
};

/**
 * The server re-checks this row with the same rule (see liveForecastPointerOf), so a mismatch
 * here would be a 409 the user can only clear by reloading.
 */
export function derivedReportRowKey(parent: DerivedParent): string | null {
  const pointer = liveForecastPointerOf(parent) as { row_key?: unknown } | null;
  return typeof pointer?.row_key === "string" ? pointer.row_key : null;
}

/**
 * Record what the parent held when a derive goes by value (no live pointer). The server
 * answers such a request 409 `pointer_required` whenever IT sees a live pointer, and that
 * disagreement is invisible from server logs: they cannot say whether the tab had no
 * pointer yet or held one it judged stale. This puts the tab's side in the bug-report ring.
 */
function recordByValueDerive(
  parent: DerivedParent & { forecastId?: string | null },
  event: ForecastEvent,
): void {
  const pointers = parent.reportPointers ?? [];
  const latest = latestForecastPointer(pointers) as { fire_ts?: unknown } | null;
  recordUiEvent({
    event: "ui.branch.derived_by_value",
    parent_forecast_id: parent.forecastId ?? null,
    event_id: event.id ?? null,
    forecast_pointers: forecastPointers(pointers).length,
    latest_fire_ts: typeof latest?.fire_ts === "string" ? latest.fire_ts : null,
    live_moment: liveContractMomentOf(parent),
    history_entries: Array.isArray(parent.history) ? parent.history.length : 0,
  });
}

/**
 * Storage read-through only: the same GET the click would issue, no run, no write.
 *
 * This runs ahead of the click's session guard, so it must not act for the session. A 401
 * here neither redirects to sign-in (the client's default) nor waits forever; it aborts its
 * own request and settles as null. The click then meets the expired session through
 * `guardForTos`.
 */
export function prefetchDerivedHeadline(
  deps: CommandDeps,
  {
    parentRunId,
    event,
    current = null,
    now = Date.now(),
  }: {
    parentRunId: string;
    event: ForecastEvent;
    current?: DerivedHeadlinePrefetch | null;
    now?: number;
  },
): DerivedHeadlinePrefetch | null {
  const parent = selectRun(deps.store.getState(), parentRunId);
  // Both cards read the same source (a and b live on one event), so one sense decides.
  if (!parent || !isDerivable(event, parent, "probability", 1)) return null;
  const rowKey = derivedReportRowKey(parent);
  if (!rowKey) return null;
  // A re-opened trigger, or an effect that runs twice (React StrictMode in development),
  // would repeat the read, so reuse the one already in hand.
  if (current && usableHeadlinePrefetch(current, rowKey, now)) return current;
  const unauthorized = new AbortController();
  const headline = readParentHeadline(deps, parent, rowKey, {
    signal: unauthorized.signal,
    onUnauthorized: () => unauthorized.abort(),
  }).catch((err: unknown): DerivedHeadline | null => {
    // A 401 aborts the read on purpose (the session is gone, not the report). Anything else is
    // a failed probe: the derive re-reads the report itself, so warn and hand back null.
    if (!unauthorized.signal.aborted) console.warn("derive_branch.headline_prefetch_failed", err);
    return null;
  });
  return { rowKey, headline, startedAt: now };
}

function usableHeadlinePrefetch(
  prefetch: DerivedHeadlinePrefetch,
  rowKey: string,
  now: number,
): boolean {
  return prefetch.rowKey === rowKey && now - prefetch.startedAt <= DERIVED_HEADLINE_PREFETCH_TTL_MS;
}

// The wait must not outlive the click: an abort resolves null at once, matching the
// click-time read bound to the same signal.
function awaitUnlessAborted(
  headline: Promise<DerivedHeadline | null>,
  signal: AbortSignal | undefined,
): Promise<DerivedHeadline | null> {
  if (!signal) return headline;
  if (signal.aborted) return Promise.resolve(null);
  return new Promise((resolve) => {
    const onAbort = () => resolve(null);
    signal.addEventListener("abort", onAbort, { once: true });
    const settle = (value: DerivedHeadline | null) => {
      signal.removeEventListener("abort", onAbort);
      resolve(value);
    };
    headline.then(settle, () => settle(null));
  });
}

/**
 * Create a completed branch from a machine trigger's persisted a/b conditionals.
 *
 * The user's branch (P2 design §4.6) opens here and ends with the derivation — unless it
 * falls back to a model run, which then continues the SAME intent (`result.intent`, passed to
 * `branchForecast` as `opts.intent`), so one click counts once.
 */
export async function deriveBranch(
  rawDeps: DeriveBranchDeps,
  args: DeriveBranchArgs,
): Promise<DeriveBranchResult> {
  const clientId = args.clientId ?? `br-${uid()}`;
  const intent = beginIntent(
    "forecast.branch",
    {
      forecast_id: clientId,
      parent_id: args.parentRunId,
      trigger_id: (args.event as { id?: string } | null)?.id ?? null,
      direction: args.newValue >= 0.5 ? "yes" : "no",
      // A derived branch keeps its parent's engine (and so does its model-run fallback).
      tier: pickedTier(selectRun(rawDeps.store.getState(), args.parentRunId)?.backend ?? null),
    },
    { origin: args.origin ?? "trigger.branch" },
  );
  intent.attachRun(clientId);
  const deps = bindIntent(rawDeps, intent);
  let result: DeriveBranchResult;
  try {
    result = await deriveBranchUnder(deps, { ...args, clientId });
  } catch (e) {
    intent.end("error");
    throw e;
  }
  if (result.fallback) return intent.id ? { ...result, intent, clientId } : result;
  intent.end(result.runId ? "ok" : args.signal?.aborted ? "cancelled" : "error");
  return result;
}

async function deriveBranchUnder(
  deps: DeriveBranchDeps,
  {
    parentRunId,
    event,
    newValue,
    forkDate,
    clientId = `br-${uid()}`,
    signal,
    onComplete,
    prefetch = null,
  }: DeriveBranchArgs,
): Promise<DeriveBranchResult> {
  const parent = selectRun(deps.store.getState(), parentRunId);
  if (!parent || !isDerivable(event, parent, "probability", newValue)) {
    // The hook already established local derivability before choosing this command.
    // Failure here is a store/source race, not server authority to buy a model run.
    return { handled: true, fallback: false, runId: null };
  }

  const reportRowKey = derivedReportRowKey(parent);
  // A live pointer supersedes the by-value row, whose contract can lag until the
  // pickup projection lands. Read that already-materialized report so the client's
  // frozen assertion and the server's selected-pointer validation use the same P(YES).
  // This is storage read-through only: it never starts a forecast/model execution.
  // A prefetch of the wrong FORM is discarded: the parent's form decides, never the value.
  const multiway = isMultiwayDerivation(parent);
  const fitsParentForm = (value: DerivedHeadline | null): value is DerivedHeadline =>
    multiway ? Array.isArray(value) : typeof value === "number";
  const usablePrefetch =
    prefetch && reportRowKey !== null && usableHeadlinePrefetch(prefetch, reportRowKey, Date.now())
      ? prefetch
      : null;
  const prefetched = usablePrefetch
    ? await awaitUnlessAborted(usablePrefetch.headline, signal)
    : null;
  let selectedHeadline: DerivedHeadline | null = fitsParentForm(prefetched) ? prefetched : null;
  if (reportRowKey && selectedHeadline == null && !signal?.aborted) {
    selectedHeadline = await readParentHeadline(deps, parent, reportRowKey, { signal });
  }
  // Cancellation during this read cannot have committed a derived row yet. Stop before
  // allocating the optimistic identity or issuing the uncancellable write-reconciliation
  // path used only after an ambiguous create request.
  if (signal?.aborted) {
    return { handled: true, fallback: false, runId: null };
  }
  const sourceSnapshot = derivedSourceSnapshot(parent, event, {
    reportRowKey,
    parentProbability: !multiway && typeof selectedHeadline === "number" ? selectedHeadline : null,
    parentMass: multiway && Array.isArray(selectedHeadline) ? selectedHeadline : null,
  });
  const identity = sourceEventIdentity(event);
  if (!sourceSnapshot || !identity || !parent?.forecastId) {
    return { handled: true, fallback: false, runId: null };
  }
  // Only a request that is actually going out as by-value is worth explaining.
  if (reportRowKey === null) recordByValueDerive(parent, event);

  const sense = derivedSense(newValue);
  const trigger = { kind: "event-mod", event, modType: "probability", newValue, forkDate };
  const built = triggerToAssumption(trigger as any) as any;
  const forkEvent = { ...built.forkEvent, _sourceEventIdentity: identity };
  const makePendingRun = () =>
    makeRun({
      runId: clientId,
      conversationId: parent.conversationId,
      parentRunId,
      status: STATUS.RUNNING,
      backend: parent.backend,
      forkDate,
      modification: built.modification,
      forkSense: sense,
      forkEvent,
      forkDerived: { source_snapshot: sourceSnapshot },
    });
  deps.store.dispatch(runCreated(makePendingRun()));
  // Same staged placeholder pass as a streamed branch (branchForecast.ts).
  deps.callbacks.startBranchAnim(clientId, 1);

  const body = {
    clientId,
    eventId: event.id,
    sense,
    forkDate,
    sourceSnapshot,
  };
  let response = await deps.services.api.createDerivedBranch(parent.forecastId, body, { signal });
  const ambiguousWithoutRow =
    !response.row &&
    (response.status === 0 ||
      response.status >= 500 ||
      (response.status >= 200 && response.status < 300));
  if (!signal?.aborted && ambiguousWithoutRow) {
    response = await deps.services.api.createDerivedBranch(parent.forecastId, body, { signal });
  }

  // A cancellation can race a server commit. The client ID makes a later explicit retry
  // idempotent. Settle that race with one uncancellable probe using the same body: it either
  // observes the committed row or creates the one deterministic row after the aborted request
  // lost. Only an authoritative row response exposes the persisted UUID required by the
  // branch-delete route:
  // a failed probe can have reached the server before the original request's late commit, so
  // deleting at that point would race the INSERT and forget the only cleanup identity. Keep
  // the errored placeholder in that ambiguous case so the existing clear action can retry the
  // stable-ID cleanup. This cancelled interaction never hydrates, summarizes, or falls back to
  // a model run.
  if (signal?.aborted) {
    // A definitive client error proves this request did not commit. Do not turn cancellation
    // into a new uncancellable write: the source could become derivable between the rejected
    // request and that probe, creating a row the cancelled interaction never authorized.
    if (!response.ok && response.status >= 400 && response.status < 500) {
      deps.store.dispatch(runRemoved(clientId));
      return { handled: true, fallback: false, runId: null };
    }
    const reconciliation =
      response.ok && response.row
        ? response
        : // `settleTriggers: false`: this probe only settles the write race before a delete, so
          // it must not start the branch's paid trigger generation.
          await deps.services.api.createDerivedBranch(
            parent.forecastId,
            { ...body, settleTriggers: false },
            {
              signal: undefined,
            },
          );
    if (!reconciliation.ok || !reconciliation.row) {
      // CLEAR may already have removed the optimistic row and drained its first
      // best-effort DELETE while this probe was pending. Re-establish only the
      // stable cleanup identity before marking it errored.
      if (!selectRun(deps.store.getState(), clientId)) {
        deps.store.dispatch(runCreated(makePendingRun()));
      }
      deps.store.dispatch(
        runErrored(
          clientId,
          new Error("Sayer is still removing this scenario. Clear it to finish."),
        ),
      );
      return { handled: true, fallback: false, runId: clientId };
    }
    const persistedBranchId = reconciliation.row.forecastId;
    if (typeof persistedBranchId !== "string" || !persistedBranchId.trim()) {
      if (!selectRun(deps.store.getState(), clientId)) {
        deps.store.dispatch(runCreated(makePendingRun()));
      }
      deps.store.dispatch(
        runErrored(
          clientId,
          new Error("Sayer is still removing this scenario. Clear it to finish."),
        ),
      );
      return { handled: true, fallback: false, runId: clientId };
    }
    await deps.services.api.deleteSharedBranch(persistedBranchId);
    deps.store.dispatch(runRemoved(clientId));
    return { handled: true, fallback: false, runId: null };
  }

  if (!response.ok || !response.row) {
    if (response.status === 409 && response.error === "not_derivable") {
      deps.store.dispatch(runRemoved(clientId));
      return { handled: false, fallback: true, runId: null };
    }
    // Deleted while its triggers were being settled: there is no branch left to show.
    if (response.status === 404 && response.error === "branch_deleted") {
      deps.store.dispatch(runRemoved(clientId));
      return { handled: true, fallback: false, runId: null };
    }
    deps.store.dispatch(
      runErrored(
        clientId,
        new Error(response.error || `Derived branch failed (${response.status})`),
      ),
    );
    return { handled: true, fallback: false, runId: clientId };
  }

  // CLEAR BRANCH can remove the optimistic placeholder while this request is in
  // flight. The DELETE it queued may reach the server before the authoritative
  // derived insert and legitimately find nothing, so a successful response must
  // re-check the live store before hydrating. If the placeholder is gone, delete
  // the just-committed row by its persisted UUID instead of resurrecting it in
  // the UI (and on the next reload).
  if (!selectRun(deps.store.getState(), clientId)) {
    const persistedBranchId = response.row.forecastId;
    if (typeof persistedBranchId !== "string" || !persistedBranchId.trim()) {
      deps.store.dispatch(runCreated(makePendingRun()));
      deps.store.dispatch(
        runErrored(
          clientId,
          new Error("Sayer is still removing this scenario. Clear it to finish."),
        ),
      );
      return { handled: true, fallback: false, runId: clientId };
    }
    await deps.services.api.deleteSharedBranch(persistedBranchId);
    return { handled: true, fallback: false, runId: null };
  }

  const completed = rowToRun(response.row as any);
  completed.conversationId = parent.conversationId;
  deps.store.dispatch(runCreated(completed));
  // The row can arrive whole, with its own triggers already settled server-side. Sweep them
  // in, then the result, like a streamed branch and a main-line forecast.
  const eventCount = (completed.events || []).length;
  deps.callbacks.startBranchAnim(clientId, Math.max(1, eventCount));
  try {
    await onComplete?.({
      runId: clientId,
      forecastId: completed.forecastId,
      parentForecastId: parent.forecastId,
      forkDate: completed.forkDate,
      modification: completed.modification,
      forkEvent: completed.forkEvent,
      events: completed.events || [],
      probability: completed.contract?.probability ?? 0.5,
      contract: completed.contract,
      serverContext: completed.serverContext,
      serverExplanation: completed.serverExplanation,
      report: completed.report,
    });
  } catch {
    // UI completion hooks must never turn a committed branch into an error.
  }
  return { handled: true, fallback: false, runId: clientId };
}
